import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react'
import {
  countLeadingZeros,
  floorToDecimal,
  formatNumberWithoutScientific,
  formatNumberWithSubscript,
} from 'utils/numbers'
import store from 'stores/store'
import {
  groupOrdersBy,
  LimitOrder,
  processLimitOrders,
} from 'utils/limitOrders'
import useInterval from 'hooks/useInterval'
import SheenLoader from '@components/loading/SheenLoader'
import GroupSize, { convertDecimals } from './GroupSize'
import { useQuery } from '@tanstack/react-query'
import { useInvalidateManifestMarket } from 'hooks/useManifestMarket'
import { fetchLastPriceForMints } from 'utils/tokens'
import { tradeTabsStore } from '../trade/tradeTabsStore'
import { useTranslation } from 'next-i18next'
import { milliseconds } from 'date-fns'

// const sizeCompacter = Intl.NumberFormat('en', {
//   maximumFractionDigits: 6,
//   notation: 'compact',
// })

// const SHOW_EXPONENTIAL_THRESHOLD = 0.00001
const MAX_ORDERS_DEPTH = 30
const DEFAULT_TICK_DECIMALS = 3

const set = store.getState().set

const Orderbook = ({ noDataClassName = '' }: { noDataClassName?: string }) => {
  const { t } = useTranslation('orderbook')
  const [bids, setBids] = useState<LimitOrder[]>([])
  const [asks, setAsks] = useState<LimitOrder[]>([])
  const [grouping, setGrouping] = useState<number | null>(null)
  const orderbookElRef = useRef<HTMLDivElement>(null)
  const invalidateManifestMarket = useInvalidateManifestMarket()
  const mint = store((s) => s.market.baseToken.mint)
  const symbol = store((s) => s.market.baseToken.symbol)
  const quoteMint = store((s) => s.market.quoteToken.mint)
  const quoteSymbol = store((s) => s.market.quoteToken.symbol)
  const manifestMarket = store((s) => s.market.manifestMarket)
  const loadingManifestMarket = store((s) => s.market.loadingManifestMarket)
  const [triedToLoadMarket, setTriedToLoadMarket] = useState(false)
  const [scrolledOnceWithData, setScrolledOnceWithData] = useState(false)

  const ordersDepth = useMemo(() => {
    if (!bids.length && !asks.length) {
      return 0
    }
    const maxOrdersLength = Math.max(bids.length, asks.length)
    return Math.min(MAX_ORDERS_DEPTH, maxOrdersLength)
  }, [bids, asks])

  const { data: lastPrice } = useQuery({
    queryKey: ['tick-decimals-ref-price', mint],
    queryFn: async () => (await fetchLastPriceForMints([mint]))?.[0] ?? null,
    cacheTime: milliseconds({ minutes: 30 }),
    staleTime: milliseconds({ minutes: 30 }),
    retry: 3,
    enabled: !!mint,
    refetchOnWindowFocus: false,
  })

  const tickDecimals = useMemo(() => {
    const leadingZeros = lastPrice?.price
      ? countLeadingZeros(lastPrice.price)
      : 0
    return (
      (manifestMarket?.tickDecimals || DEFAULT_TICK_DECIMALS) + leadingZeros
    )
  }, [manifestMarket, lastPrice])

  const groupingToUse = useMemo(() => {
    if (!grouping && tickDecimals) {
      return convertDecimals(tickDecimals)
    }
    return grouping
  }, [grouping, tickDecimals])

  const updateOrderbook = useCallback(() => {
    if (!manifestMarket || !groupingToUse) {
      return
    }

    try {
      const rawAsks = manifestMarket.market
        .asks()
        .map(
          (order) => [order.tokenPrice, Number(order.numBaseTokens)] as const,
        )
      const rawBids = manifestMarket.market
        .bids()
        .map(
          (order) => [order.tokenPrice, Number(order.numBaseTokens)] as const,
        )
      const groupedAsks = groupOrdersBy(groupingToUse, 'asks', rawAsks)
      const groupedBids = groupOrdersBy(groupingToUse, 'bids', rawBids)
      const asks = processLimitOrders('asks', groupedAsks, MAX_ORDERS_DEPTH)
      const bids = processLimitOrders('bids', groupedBids, MAX_ORDERS_DEPTH)
      setAsks(asks)
      setBids(bids)
    } catch (e) {
      console.error('Error updating orderbook', e)
    }
  }, [manifestMarket, groupingToUse])

  useInterval(async () => {
    if (!triedToLoadMarket) {
      setTriedToLoadMarket(true)
    }
    void invalidateManifestMarket(mint, quoteMint)
    updateOrderbook()
  }, 3000)

  const spread = useMemo(() => {
    if (bids && bids.length > 0 && asks && asks.length > 0) {
      const highestBid = bids[0].price
      const lowestAsk = asks[asks.length - 1].price
      if (highestBid === 0) {
        return lowestAsk
      }
      const spreadPercentage = Math.max(0, lowestAsk / highestBid - 1) * 100
      return spreadPercentage.toFixed(2) + '%'
    } else {
      return ''
    }
  }, [bids, asks])

  const scrollOrderbookToMidpoint = useCallback(() => {
    const element = orderbookElRef.current
    if (!element) {
      return
    }

    element.scrollTop = (element.scrollHeight - element.clientHeight) / 2
  }, [])

  useEffect(() => {
    window.addEventListener('resize', scrollOrderbookToMidpoint)
    return () => window.removeEventListener('resize', scrollOrderbookToMidpoint)
  }, [scrollOrderbookToMidpoint])

  // center orderbook once initially
  useEffect(() => {
    if (
      scrolledOnceWithData ||
      bids == null ||
      bids.length === 0 ||
      asks == null ||
      asks.length === 0
    ) {
      return
    }
    scrollOrderbookToMidpoint()
    setScrolledOnceWithData(true)
  }, [scrollOrderbookToMidpoint, scrolledOnceWithData, asks, bids])

  // center orderbook on market change
  useEffect(scrollOrderbookToMidpoint, [
    mint,
    quoteMint,
    orderbookElRef,
    scrollOrderbookToMidpoint,
  ])

  const onGroupSizeChange = useCallback(
    (groupSize: number) => {
      setGrouping(groupSize)
      // if (market) {
      //   const updatedOrderbook = formatOrderbookData(
      //     currentOrderbookData?.current?.bids,
      //     currentOrderbookData?.current?.asks,
      //     ordersDepth,
      //     market,
      //     groupSize,
      //     usersOpenOrderPrices(market),
      //   )
      //   setOrderbookData(updatedOrderbook)
      // }
      scrollOrderbookToMidpoint()
    },
    [scrollOrderbookToMidpoint],
  )

  const makeOrderbookRows = (
    ordersKind: 'asks' | 'bids',
    orders: LimitOrder[],
    ordersDepth: number,
  ) => {
    if (orders.length === 0) {
      return <div className="h-[20px]" />
    }

    const side = ordersKind === 'asks' ? 'sell' : 'buy'

    // make array with nulls to fill the orderbook to ordersDepth
    const ordersExtension = Array.from(
      { length: Math.max(0, ordersDepth - orders.length) },
      () => null as LimitOrder | null,
    )
    const depthSizedOrders =
      ordersExtension.length === 0
        ? orders
        : ordersKind === 'asks'
          ? ordersExtension.concat(orders)
          : (orders as (LimitOrder | null)[]).concat(ordersExtension)

    return depthSizedOrders.map((order, idx) => {
      // const isUsersOrder =
      //   publicKey && order?.trader ? publicKey.equals(order.trader) : false
      const isUsersOrder = false
      return (
        <div className="h-[20px]" key={`${side}-${idx}`}>
          {order ? (
            <OrderbookRow
              hasOpenOrder={isUsersOrder}
              price={order.price}
              size={order.size}
              side={side}
              sizePercent={order.sizePercent}
              cumulativeSizePercent={order.cumulativeSizePercent}
            />
          ) : null}
        </div>
      )
    })
  }

  return loadingManifestMarket && !triedToLoadMarket ? (
    <div
      className={`flex h-full flex-col justify-center overflow-hidden ${noDataClassName}`}
    >
      {Array.from({ length: 10 }, (_, i) => (
        <SheenLoader
          className="rounded-none"
          key={`sell-${i}`}
          style={{ width: `${100 - i * 10}%` }}
        >
          <div className={`h-5 ${i % 2 ? 'bg-th-bkg-2' : 'bg-th-bkg-3'}`} />
        </SheenLoader>
      ))}
      {Array.from({ length: 11 }, (_, i) => (
        <SheenLoader
          className="rounded-none"
          key={`buy-${i}`}
          style={{ width: `${i * 10}%` }}
        >
          <div className={`h-5 ${i % 2 ? 'bg-th-bkg-2' : 'bg-th-bkg-3'}`} />
        </SheenLoader>
      ))}
    </div>
  ) : manifestMarket?.market && groupingToUse ? (
    <div className="h-full">
      <div className="flex h-8 items-center justify-between border-b border-th-bkg-3 px-2 lg:px-0">
        <p className="text-xs">{t('grouping')}</p>
        <GroupSize
          tickDecimals={tickDecimals}
          onChange={onGroupSizeChange}
          value={groupingToUse}
        />
      </div>
      <div className="grid grid-cols-2 px-2 pb-1 pt-1.5 text-xxs text-th-fgd-4">
        <div className="col-span-1">{t('price')}</div>
        <div className="col-span-1 flex items-center justify-end space-x-2">
          <span className="text-right">{t('size')}</span>
        </div>
      </div>
      <div className="flex h-[calc(100%-62px)] flex-col justify-center lg:h-[calc(100%-48px)]">
        <div
          className="hide-scroll relative overflow-y-scroll"
          ref={orderbookElRef}
        >
          {makeOrderbookRows('asks', asks, ordersDepth)}
          <div className="my-1 flex items-center justify-between bg-th-bkg-2 px-2 py-1 text-xs text-th-fgd-4">
            <p className="text-xxs">{t('spread')}</p>
            <div className="font-mono">
              <span className="text-th-fgd-3">{spread}</span>
            </div>
          </div>
          {makeOrderbookRows('bids', bids, ordersDepth)}
        </div>
      </div>
    </div>
  ) : (
    <div
      className={`flex h-full flex-col justify-center overflow-hidden ${noDataClassName}`}
    >
      <div className="relative">
        {Array.from({ length: 10 }, (_, i) => (
          <div
            className={`h-5 ${
              i % 2 ? 'bg-th-bkg-2' : 'bg-th-bkg-3'
            } opacity-50`}
            style={{ width: `${100 - i * 10}%` }}
            key={`sell-${i}`}
          />
        ))}
        {Array.from({ length: 11 }, (_, i) => (
          <div
            className={`h-5 ${
              i % 2 ? 'bg-th-bkg-2' : 'bg-th-bkg-3'
            } opacity-50`}
            style={{ width: `${i * 10}%` }}
            key={`buy-${i}`}
          />
        ))}
        <div className="absolute left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2">
          <p className="text-center">
            {t('create-market', { base: symbol, quote: quoteSymbol })}
          </p>
        </div>
      </div>
    </div>
  )
}

const OrderbookRow = memo(function OrderbookRow({
  side,
  price,
  size,
  sizePercent,
  hasOpenOrder,
  cumulativeSizePercent,
}: {
  side: 'buy' | 'sell'
  price: number
  size: number
  sizePercent: number
  cumulativeSizePercent: number
  hasOpenOrder: boolean
}) {
  const element = useRef<HTMLDivElement>(null)
  const manifestMarket = store((s) => s.market.manifestMarket)
  // const flashClassName = side === 'sell' ? 'red-flash' : 'green-flash'

  // useEffect(() => {
  //   animationSettings['orderbook-flash'].active &&
  //     !element.current?.classList.contains(`${flashClassName}`) &&
  //     element.current?.classList.add(`${flashClassName}`)
  //   const id = setTimeout(
  //     () =>
  //       element.current?.classList.contains(`${flashClassName}`) &&
  //       element.current?.classList.remove(`${flashClassName}`),
  //     500,
  //   )
  //   return () => clearTimeout(id)
  // }, [price, size])

  const [formattedPrice, tickDecimals] = useMemo(() => {
    const leadingZeros = price < 0.1 ? countLeadingZeros(price) : 0
    const decimals =
      (manifestMarket?.tickDecimals || DEFAULT_TICK_DECIMALS) + leadingZeros
    return [floorToDecimal(price, decimals).toNumber(), decimals]
  }, [manifestMarket, price])

  const formattedSize = useMemo(() => {
    const decimals = manifestMarket ? manifestMarket?.minOrderSizeDecimals : 5
    const minSize = floorToDecimal(size, decimals).toNumber()
    return minSize
  }, [size, manifestMarket])

  // set limit form price and update calculated output amount
  const handlePriceClick = useCallback(() => {
    tradeTabsStore
      .getState()
      .actions.selectTab({ tab: 'limit', preserveInitialPrice: true })

    set((s) => {
      s.trade.limitForm.price = formatNumberWithoutScientific(formattedPrice)
    })
    store
      .getState()
      .actions.setCalculatedLimitFormOutputAmount(
        store.getState().trade.limitForm.inputAmount,
        String(formattedPrice),
        tickDecimals,
      )
  }, [formattedPrice, tickDecimals])

  // set limit form input/output size _and_ price and update calculated output/input amount
  const handleSizeClick = useCallback(() => {
    tradeTabsStore
      .getState()
      .actions.selectTab({ tab: 'limit', preserveInitialPrice: true })

    const isBid =
      store.getState().swap.inputToken.mint ===
      store.getState().market.quoteToken.mint

    set((s) => {
      s.trade.limitForm.price = formatNumberWithoutScientific(formattedPrice)
      if (isBid) {
        s.trade.limitForm.outputAmount =
          formatNumberWithoutScientific(formattedSize)
      } else {
        s.trade.limitForm.inputAmount =
          formatNumberWithoutScientific(formattedSize)
      }
    })

    if (isBid) {
      store
        .getState()
        .actions.setCalculatedLimitFormValuesFromOutputAmount(
          formatNumberWithoutScientific(formattedSize),
        )
    } else {
      store
        .getState()
        .actions.setCalculatedLimitFormOutputAmount(
          String(formattedSize),
          store.getState().trade.limitForm.price,
          tickDecimals,
        )
    }
  }, [formattedPrice, formattedSize, tickDecimals])

  // const groupingDecimalCount = useMemo(
  //   () => getDecimalCount(grouping),
  //   [grouping],
  // )

  // const handleMouseOver = useCallback(() => {
  //   const { set } = mangoStore.getState()
  //   if (averagePrice && cumulativeSize && cumulativeValue) {
  //     set((state) => {
  //       state.orderbookTooltip = {
  //         averagePrice,
  //         cumulativeSize,
  //         cumulativeValue,
  //         side,
  //       }
  //     })
  //   }
  // }, [averagePrice, cumulativeSize, cumulativeValue])

  // const handleMouseLeave = useCallback(() => {
  //   const { set } = mangoStore.getState()
  //   set((state) => {
  //     state.orderbookTooltip = undefined
  //   })
  // }, [])

  return manifestMarket ? (
    <div
      className={`relative flex h-[20px] cursor-pointer justify-between border-b border-b-th-bkg-1 text-sm`}
      ref={element}
      // onMouseOver={handleMouseOver}
      // onMouseLeave={handleMouseLeave}
    >
      <>
        <div className="flex size-full items-center justify-between text-th-fgd-3 hover:bg-th-bkg-2">
          <div
            className="z-10 flex size-full items-center pl-2 hover:underline"
            onClick={handlePriceClick}
          >
            <span className="w-full font-mono text-xs text-th-fgd-2">
              {formatNumberWithSubscript(
                formatNumberWithoutScientific(formattedPrice),
              )}
            </span>
          </div>
          <div
            className="flex size-full items-center justify-start pr-2 hover:underline"
            onClick={handleSizeClick}
          >
            <div
              style={{ fontFeatureSettings: 'zero 1' }}
              className={`z-10 w-full text-right font-mono text-xs ${
                hasOpenOrder ? 'text-th-active' : ''
              }`}
            >
              <span className="text-th-fgd-2">
                {formatNumberWithoutScientific(formattedSize)}
              </span>
            </div>
          </div>
        </div>

        <Line
          className={`absolute left-0 opacity-30 brightness-125 ${
            side === 'buy' ? `bg-th-up-muted` : `bg-th-down-muted`
          }`}
          data-width={Math.max(cumulativeSizePercent, 0.5) + '%'}
        />
        <Line
          className={`absolute left-0 opacity-40 ${
            side === 'buy' ? `bg-th-up` : `bg-th-down`
          }`}
          data-width={Math.max(sizePercent, 0.1) + '%'}
        />
      </>
    </div>
  ) : null
})

const Line = (props: {
  className: string
  invert?: boolean
  'data-width': string
}) => {
  return (
    <div
      className={`${props.className}`}
      style={{
        textAlign: props.invert ? 'left' : 'right',
        height: '100%',
        width: `${props['data-width'] ? props['data-width'] : ''}`,
      }}
    />
  )
}

export default Orderbook
